<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import type { ITableColumnsOptions } from '@modules/tables/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import { useFormatDate } from '@modules/core/composables/datetime';
import { FORMAT_MONTH } from '@modules/core/composables/datetime/constants';
import VDownloadReportButton from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import { useDownloadReport } from '@modules/download-report/composables';
import {
  FINANCE_PERMISSIONS,
  TRANSLATION_GROUP_FINANCE,
} from '@modules/finance/constants';
import {
  useDownloadFinanceMonthReportMutation,
} from '@modules/finance/submodules/month-report/mutations';
import { useFinanceMonthListQuery } from '@modules/finance/submodules/month-report/queries';
import { ADMIN_FINANCE_MONTH_DEDUCTION_ROUTE_NAME } from '@modules/finance/submodules/month-report/submodules/deduction/constants';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';
import {
  isString,
} from '@utils/guards';

import { useFinanceMonthListPage } from './composables';

const route = useRoute();
const { formatDate } = useFormatDate();

const date = computed<string>(() => (isString(route.params.date) ? route.params.date : ''));
const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
  setData,
} = useTableData(useFinanceMonthListQuery, date);

const {
  isReportLoading,
  onDownloadReport,
} = useDownloadReport(useDownloadFinanceMonthReportMutation(date), tableState);

const {
  isAdminNamespace,
  backRouteName,
  isInProcess,
  isCloseMonthAvailable,
  isApproveMonthLoading,
  onApproveFinanceMonth,
  isApproved,
  isPayMonthLoading,
  onPayFinanceMonth,
  multiselectOptions,
  onMultiselectAction,
} = useFinanceMonthListPage(
  data,
  tableState,
  date,
  setData,
);

const columnOptions = computed<ITableColumnsOptions>(() => ({
  aasm_state: {
    localization: {
      translationPath: isAdminNamespace.value
        ? 'admin.partnership_program.bookkeeping.transfer_payment_statuses.'
        : 'admin.partnership_program.bookkeeping.aasm_state.',
    },
  },
  status: {
    localization: {
      translationPath: 'admin.partnership_program.bookkeeping.aasm_state.',
    },
  },
  affiliate_username: {
    defaultMobilePreview: true,
  },
}));
</script>

<template>
  <VCard v-data-qa-auto-id="'FinanceMonthListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="date ? formatDate(date, FORMAT_MONTH) : ''"
      :multiselect="multiselectOptions"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @multiselect-action="onMultiselectAction"
      @refresh="refreshTableData"
    >
      <template
        v-if="$hasPermission(FINANCE_PERMISSIONS.DEDUCTION)"
        #recordAction="{ listItem }"
      >
        <VIconButton
          icon="pi-dollar"
          :to="{
            name: ADMIN_FINANCE_MONTH_DEDUCTION_ROUTE_NAME,
            params: {
              date,
              id: listItem.affiliate_id,
            },
          }"
        />
      </template>
      <template
        #footerButtons
      >
        <div
          v-if="$hasPermission(FINANCE_PERMISSIONS.CHANGE_STATUS) && isInProcess"
          :title="isCloseMonthAvailable ? '' : $getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_CLOSE_MONTH_DESCRIPTION')"
        >
          <VButton
            :label="$getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_APPROVED')"
            :loading="isApproveMonthLoading"
            :disabled="!isCloseMonthAvailable"
            @click="onApproveFinanceMonth"
          />
        </div>
        <VButton
          v-if="$hasPermission(FINANCE_PERMISSIONS.CHANGE_STATUS) && isApproved"
          :loading="isPayMonthLoading"
          :label="$getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_PAID')"
          @click="onPayFinanceMonth"
        />
        <VDownloadReportButton
          v-if="$hasPermission(FINANCE_PERMISSIONS.REPORT)"
          :label="$getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_DOWNLOAD_REPORT')"
          :loading="isReportLoading"
          @download="onDownloadReport"
        />
        <VBackButton
          :label="$getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_BACK')"
          :to="{ name: backRouteName }"
        />
      </template>
    </VTable>
  </VCard>
</template>
